import { AppContext } from "App";

import { AuthManagerChangePasswordType, CheckLoginResultData, IAuthClient, IChangePasswordParams, ICheckLoginParams, ILoginCredentials } from "@multiweb/clients/auth";
import { IUserClient } from "@multiweb/clients/user";
import { ISectionLogger } from "@multiweb/logging";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { ServiceWorkerMessageType } from "../../../Global/Base/ServiceWorkerMessages";
import { ServiceWorkerConnector } from "../../Basic/ServiceWorkerConnector";
import { ApplicationStorage, AppStorageItemType } from "../ApplicationStorage";
import { IAuthManager } from "./IAuthManager";

export interface IDevLoginOptions {
    userName?: string;
    password?: string;
}

export class StandardAuthManager implements IAuthManager {
    //#region Fields
    private readonly _authClient: IAuthClient;
    private readonly _userClient: IUserClient;
    private readonly _clientCode: string;
    private readonly _devLoginOptions: IDevLoginOptions;
    private readonly _logger: ISectionLogger;
    //#endregion Fields

    constructor(authClient: IAuthClient, userClient: IUserClient, clientCode: string, logger: ISectionLogger, devLoginOptions: IDevLoginOptions = {}) {
        this._authClient = authClient;
        this._userClient = userClient;
        this._clientCode = clientCode;
        this._devLoginOptions = devLoginOptions;
        this._logger = logger;
    }

    //#region Methods
    public async changePassword(changePasswordParams: IChangePasswordParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<AuthManagerChangePasswordType> {
        try {
            await this._authClient.changeExpiredPassword(changePasswordParams, cancellationToken);
            return { status: true };
        } catch (e) {
            return { status: false, error: e };
        }
    }

    private async _setAppSpecKey(value: string): Promise<void> {
        ApplicationStorage.set(`SpecKey-${AppContext.instance.appId}`, value); // ` SpecKey = AuthID, skryto kvůli bezpečnosti / penetračním testům
        // ServiceWorkeru se pošle authId, se kterým se má volat heartBeat
        ServiceWorkerConnector.sendMessage({ type: ServiceWorkerMessageType.updateAuthId, payload: { authId: value } });
    }

    public async checkLogin(checkLogonParams: ICheckLoginParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        const result = await this._checkLogon(checkLogonParams, cancellationToken); //`

        await this._setAppSpecKey(result.authId);

        return result.isLoggedIn;
    }

    public async redirectToLoginPage(): Promise<void> {
        try {
            await this._tryDevLogin();

            await this._userClient.getCurrentUserInfo();
            location.href = "main" + location.search;
        } catch (e) {
            if (e.message !== "Session time out!") // Uživatel není přihlášený - OK, pokračujeme na přihlášení
                this._logger.error(e);

            await this._tryLoginWithWindowsAuth();
        }
    }

    protected async _tryDevLogin(): Promise<void> {
        if (!this._devLoginOptions?.userName || !this._devLoginOptions?.password)
            return;

        this._logger.info(`Auto Login with user: '${this._devLoginOptions.userName}'`); //`
        await this.login({
            userName: this._devLoginOptions.userName,
            password: this._devLoginOptions.password,
            language: "CZ",
        });
    }

    protected async _tryLoginWithWindowsAuth(): Promise<void> {
        // zjištění, jestli se uživatel naposledy přihlašoval pomocí windows autentizace
        const useWinAuth = ApplicationStorage.get(AppStorageItemType.winAuthUsed);
        if (useWinAuth == undefined || useWinAuth === true.toString()) {
            try {
                const result = await this.checkLogin({ winAuth: true, sso: true });
                if (result) {
                    ApplicationStorage.set(AppStorageItemType.winAuthUsed, true.toString());
                    this._redirectToMainPage();
                    return;
                }
            } catch {
                // nic, pokračuje se klasickým přihlášením bez WinAuth
            }
        }

        this._redirectToLoginPage();
    }

    protected _redirectToLoginPage(): void {
        this._authClient.redirectToLoginPage(this._clientCode, location.search);
    }

    protected _redirectToMainPage(): void {
        location.href = "main" + location.search;
    }

    public async login(loginCredentials: ILoginCredentials, winAuth: boolean = true, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        try {
            const checkLogonParams = { language: loginCredentials.language, winAuth, sso: true };
            await this._checkLogon(checkLogonParams, cancellationToken);
        } catch (e) {
            // uživatele se nepodařilo přihlásit windows autentizací
        }

        const result = await this._authClient.login(loginCredentials, cancellationToken);
        await this._setAppSpecKey(result.authId);//TODO: #28178
    }

    public loginAnonymous(language: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        return this._authClient.loginAnonymously(language, cancellationToken);
    }

    public async logout(cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        ApplicationStorage.remove(AppStorageItemType.winAuthUsed);
        await this._authClient.logout(this._clientCode, cancellationToken);
    }

    private _checkLogon(checkLogonParams: ICheckLoginParams, cancellationToken: CancellationToken = CancellationToken.none): Promise<CheckLoginResultData> {
        return this._authClient.checkLogin(checkLogonParams, cancellationToken);
    }

    //#endregion Methods
}
